import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Card, Table, Empty, Tag, Button, Typography, Modal, 
  Select, App, Input, Form, DatePicker, Switch, Space, Tooltip,
  InputNumber, Slider, Spin, Checkbox
} from 'antd';
import { EditOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { useEvent } from '../../hooks/useEvent';
import { useAuth } from '../../hooks/useAuth';
import { isEventActive as checkEventActive } from '../../utils/eventSource';
import { useCollectionFreshness } from '../../hooks/useCollectionFreshness';
import { RainForecastsTimeline } from '../../components/RainForecastsTimeline';
import forecastService, { type ForecastRain } from '../../api/forecastService';
import type { Session } from '../../api/eventsService';
import { sessionTypeValueForSession } from '../../utils/rainForecastSelection';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;
const { TextArea } = Input;

// Rain type options
const rainTypeOptions = [
  { value: 0, label: '☀️ No rain expected' },
  { value: 1, label: '🌥️ Rain expected' },
  { value: 2, label: '🌧️ Currently raining' },
];

// Rain pattern options
const rainPatternOptions = [
  { value: 'steady', label: 'Steady' },
  { value: 'intermittent', label: 'Intermittent' },
];

// Legacy portal session codes (labels for historical / fallback UI)
const LEGACY_SESSION_TYPE_OPTIONS = [
  { value: 'FREE', label: 'Free Practice' },
  { value: 'qualifications', label: 'Qualifications' },
  { value: 'sprint', label: 'Sprint' },
  { value: 'sprint_qualifications', label: 'Sprint Qualifications' },
  { value: 'race', label: 'Race' },
];

function buildSessionSelectOptions(sessions: Session[], circuitTz: string): { value: string; label: string }[] {
  const usedValues = new Set<string>();
  const out: { value: string; label: string }[] = [];

  for (const session of sessions) {
    const candidate = sessionTypeValueForSession(session, circuitTz);
    let value = candidate;
    let n = 2;
    while (usedValues.has(value)) {
      value = `${candidate} (${n++})`;
    }
    usedValues.add(value);
    out.push({ value, label: value });
  }

  return out;
}

// Rain status colors from legacy portal
const RAIN_COLORS = {
  green: '#2dd598',   // No rain expected
  orange: '#ff8e01',  // Rain expected
  red: '#ff3b3c',     // Currently raining
  yellow: '#e4e14d',  // Drops at times
};

// Calculate rain status based on rain_type and few_drops
const getRainStatus = (rainType: number | null, fewDrops: boolean | null): 0 | 1 | 2 | 3 => {
  if (rainType === 0 && fewDrops) return 3;
  if (rainType === 2) return 2;
  if (rainType === 1) return 1;
  return 0;
};

const getStatusColor = (status: 0 | 1 | 2 | 3): string => {
  switch (status) {
    case 0: return RAIN_COLORS.green;
    case 1: return RAIN_COLORS.orange;
    case 2: return RAIN_COLORS.red;
    case 3: return RAIN_COLORS.yellow;
    default: return RAIN_COLORS.green;
  }
};

const getStatusLabel = (status: 0 | 1 | 2 | 3): string => {
  switch (status) {
    case 0: return 'No rain';
    case 1: return 'Rain expected';
    case 2: return 'Raining';
    case 3: return 'Few drops';
    default: return 'Unknown';
  }
};

// Get background color with opacity for the tag (matching RainForecastHeader card style)
const getTagBackground = (status: 0 | 1 | 2 | 3): string => {
  const color = getStatusColor(status);
  const opacity = 0.2;
  
  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const RainForecastsPage = () => {
  const { message, modal } = App.useApp();
  const { selectedEvent } = useEvent();
  const { authState } = useAuth();
  
  const [forecasts, setForecasts] = useState<ForecastRain[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // For refresh button indicator
  const [error, setError] = useState<string | null>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingForecast, setEditingForecast] = useState<ForecastRain | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [form] = Form.useForm();

  // Table container ref for dynamic scroll height
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

  // Check if user has root role
  const hasAdminRole = authState.user?.role === 'root';
  
  // Get circuit timezone
  const circuitTimeZone = selectedEvent?.circuit?.time_zone || 'UTC';

  // Check if this is a non-legacy event
  const isNonLegacyEvent = selectedEvent && !selectedEvent.legacy;
  
  // Check if this is a demo event
  const isDemo = selectedEvent?.demo ?? false;

  // Check if the event is currently active (ongoing). Demo events stay manageable after end for debugging.
  // Uses circuit timezone for consistent behavior across the app
  const isEventActive = checkEventActive(selectedEvent);

  // Poll metadata timestamps to detect external rain forecast updates
  const metadataTimestamps = useCollectionFreshness(['forecast_rain'], {
    interval: 30_000,
    enabled: isEventActive,
  });
  const lastRainFetchTimeRef = useRef<string>('');

  // User can manage forecasts if root, non-legacy, and (event active or demo sandbox)
  const canManageForecasts = hasAdminRole && isNonLegacyEvent && (isEventActive || isDemo);

  // For non-root users, hide forecasts with a value_date in the future
  const displayedForecasts = useMemo(() => {
    if (hasAdminRole) return forecasts;
    const now = dayjs().utc().valueOf();
    return forecasts.filter((f) => dayjs.utc(f.value_date).valueOf() <= now);
  }, [forecasts, hasAdminRole]);

  const sessionSelectOptions = useMemo(() => {
    if (selectedEvent && !selectedEvent.legacy && selectedEvent.sessions?.length) {
      return buildSessionSelectOptions(selectedEvent.sessions, circuitTimeZone);
    }
    return LEGACY_SESSION_TYPE_OPTIONS;
  }, [selectedEvent, circuitTimeZone]);

  const defaultSessionType = useMemo(() => {
    if (selectedEvent && !selectedEvent.legacy && selectedEvent.sessions?.length) {
      const opts = buildSessionSelectOptions(selectedEvent.sessions, circuitTimeZone);
      return opts[0]?.value ?? 'FREE';
    }
    return 'FREE';
  }, [selectedEvent, circuitTimeZone]);

  const modalSessionSelectOptions = useMemo(() => {
    const st = editingForecast?.session_type;
    if (!st || sessionSelectOptions.some((o) => o.value === st)) {
      return sessionSelectOptions;
    }
    return [...sessionSelectOptions, { value: st, label: st }];
  }, [sessionSelectOptions, editingForecast?.session_type]);

  // Fetch forecasts
  const fetchForecasts = useCallback(async (showLoading: boolean = true, showRefreshing: boolean = false) => {
    if (!selectedEvent) {
      setForecasts([]);
      return;
    }

    // Only show loading overlay if explicitly requested (for initial loads)
    // For background refreshes, we update data silently
    if (showLoading) {
      setLoading(true);
    }
    if (showRefreshing) {
      setRefreshing(true);
    }
    setError(null);

    try {
      const source = selectedEvent.demo 
        ? 'demo' 
        : selectedEvent.legacy 
          ? 'legacy' 
          : 'prod';

      const response = await forecastService.getForecastRain({
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        source,
        include_deleted: includeDeleted,
      });

      // Sort by value_date descending (most recent first)
      const sorted = [...(response.forecasts || [])].sort((a, b) => {
        return new Date(b.value_date).getTime() - new Date(a.value_date).getTime();
      });

      setForecasts(sorted);
    } catch (err) {
      console.error('Failed to fetch rain forecasts:', err);
      setError('Failed to load rain forecasts');
      setForecasts([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      if (showRefreshing) {
        setRefreshing(false);
      }
    }
  }, [selectedEvent, includeDeleted]);

  useEffect(() => {
    fetchForecasts(true); // Show loading on initial load
  }, [fetchForecasts]);

  // Trigger refetch when metadata timestamps indicate new rain forecast data
  useEffect(() => {
    const latest = metadataTimestamps.forecast_rain;
    if (latest && latest > lastRainFetchTimeRef.current) {
      lastRainFetchTimeRef.current = latest;
      fetchForecasts(false, true);
    }
  }, [metadataTimestamps, fetchForecasts]);

  // Measure table container and set table body scroll height (reserve for pagination)
  const TABLE_SCROLL_RESERVE_PX = 80;
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const updateScrollY = () => {
      if (!el.isConnected) return;
      const height = el.clientHeight;
      setTableScrollY(Math.max(200, height - TABLE_SCROLL_RESERVE_PX));
    };
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, displayedForecasts.length]);

  // Refresh forecasts when tab becomes visible or window gains focus (for active events)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isEventActive) {
        console.log('[RainForecasts] Tab became visible, refreshing forecasts');
        fetchForecasts(false, true); // Silent refresh - show button spinner but no table loading overlay
      }
    };

    // Also handle window focus event for when returning from another application
    // (visibilitychange may not fire in all browsers when switching apps)
    const handleWindowFocus = () => {
      if (isEventActive) {
        console.log('[RainForecasts] Window gained focus, refreshing forecasts');
        fetchForecasts(false, true); // Silent refresh - show button spinner but no table loading overlay
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [isEventActive, fetchForecasts]);

  const handleCreate = () => {
    setEditingForecast(null);
    form.resetFields();
    
    const nowInCircuitTz = dayjs().tz(circuitTimeZone).second(0).millisecond(0);
    
    form.setFieldsValue({
      session_type: defaultSessionType,
      is_active: true,
      rain_type: 0,
      few_drops: false,
      value_date: nowInCircuitTz,
      message: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (forecast: ForecastRain) => {
    setEditingForecast(forecast);
    
    const valueDate = forecast.value_date 
      ? dayjs.utc(forecast.value_date).tz(circuitTimeZone)
      : undefined;
    
    form.setFieldsValue({
      session_type: forecast.session_type || defaultSessionType,
      is_active: forecast.is_active,
      message: forecast.message,
      rain_type: forecast.rain_type ?? 0,
      few_drops: forecast.few_drops ?? false,
      rain_speed_kph: forecast.rain_speed_kph,
      rain_direction_deg: forecast.rain_direction_deg,
      rain_intensity: forecast.rain_intensity,
      rain_pattern: forecast.rain_pattern,
      rain_start_time: forecast.rain_start_time,
      rain_end_time: forecast.rain_end_time,
      value_date: valueDate,
    });
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingForecast(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      
      setSubmitting(true);

      // Convert value_date to UTC ISO string
      const valueDateUtc = values.value_date 
        ? dayjs(values.value_date).utc().toISOString()
        : new Date().toISOString();

      if (editingForecast) {
        await forecastService.updateForecastRain({
          season_year: selectedEvent!.season_year,
          circuit_id: selectedEvent!.circuit_id,
          start_date: selectedEvent!.start_date,
          value_date: editingForecast.value_date,
          created_at: editingForecast.created_at,
          session_type: values.session_type,
          is_active: values.is_active,
          message: values.message,
          rain_type: values.rain_type,
          few_drops: values.few_drops,
          rain_speed_kph: values.rain_speed_kph || null,
          rain_direction_deg: values.rain_direction_deg || null,
          rain_intensity: values.rain_intensity || null,
          rain_pattern: values.rain_pattern || null,
          rain_start_time: values.rain_start_time || null,
          rain_end_time: values.rain_end_time || null,
          new_value_date: valueDateUtc !== editingForecast.value_date ? valueDateUtc : undefined,
          demo: isDemo,
        });
        message.success('Rain forecast updated successfully');
      } else {
        const forecastData = {
          season_year: selectedEvent!.season_year,
          circuit_id: selectedEvent!.circuit_id,
          start_date: selectedEvent!.start_date,
          session_type: values.session_type,
          is_active: values.is_active,
          message: values.message,
          rain_type: values.rain_type,
          few_drops: values.few_drops,
          rain_speed_kph: values.rain_speed_kph || null,
          rain_direction_deg: values.rain_direction_deg || null,
          rain_intensity: values.rain_intensity || null,
          rain_pattern: values.rain_pattern || null,
          rain_start_time: values.rain_start_time || null,
          rain_end_time: values.rain_end_time || null,
          value_date: valueDateUtc,
          demo: isDemo,
        };
        await forecastService.createForecastRain(forecastData);
        message.success('Rain forecast created successfully');
      }

      setIsModalOpen(false);
      setEditingForecast(null);
      form.resetFields();
      fetchForecasts(false);
    } catch (err: unknown) {
      console.error('Failed to save rain forecast:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save rain forecast';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (forecast: ForecastRain) => {
    modal.confirm({
      title: 'Delete Rain Forecast',
      content: `Are you sure you want to delete the rain forecast from ${dayjs.utc(forecast.value_date).tz(circuitTimeZone).format('DD/MM HH:mm')}?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await forecastService.deleteForecastRain({
            season_year: forecast.season_year,
            circuit_id: forecast.circuit_id,
            start_date: forecast.start_date,
            value_date: forecast.value_date,
            created_at: forecast.created_at,
            source: isDemo ? 'demo' : 'prod',
          });
          message.success('Rain forecast deleted successfully');
          fetchForecasts(false);
        } catch (err: unknown) {
          console.error('Failed to delete rain forecast:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to delete rain forecast';
          message.error(errorMessage);
        }
      },
    });
  };

  const handleRestore = async (forecast: ForecastRain) => {
    try {
      await forecastService.setForecastRainDeleted({
        season_year: forecast.season_year,
        circuit_id: forecast.circuit_id,
        start_date: forecast.start_date,
        value_date: forecast.value_date,
        created_at: forecast.created_at,
        deleted: false,
        demo: isDemo,
      });
      message.success('Rain forecast restored successfully');
      fetchForecasts(false);
    } catch (err: unknown) {
      console.error('Failed to restore rain forecast:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore rain forecast';
      message.error(errorMessage);
    }
  };

  // Table columns
  const columns: ColumnsType<ForecastRain> = [
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, record) => {
        const status = getRainStatus(record.rain_type, record.few_drops);
        const color = getStatusColor(status);
        const label = getStatusLabel(status);
        const background = getTagBackground(status);
        return (
          <Tag 
            style={{ 
              color: color, 
              fontWeight: 600, 
              fontSize: 11,
              background: background,
              border: `1px solid ${color}40`,
            }}
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Value Date',
      dataIndex: 'value_date',
      key: 'value_date',
      width: 140,
      render: (value: string) => {
        const dt = dayjs.utc(value).tz(circuitTimeZone);
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text style={{ fontSize: 12 }}>{dt.format('DD MMM YYYY')}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{dt.format('HH:mm:ss')}</Text>
          </div>
        );
      },
    },
    {
      title: 'Session',
      dataIndex: 'session_type',
      key: 'session_type',
      width: 220,
      ellipsis: true,
      render: (value: string | null) => {
        if (!value) return <Text style={{ fontSize: 12 }}>-</Text>;
        if (selectedEvent?.legacy) {
          const option = LEGACY_SESSION_TYPE_OPTIONS.find((o) => o.value === value);
          return (
            <Tooltip title={option?.label ?? value}>
              <Text style={{ fontSize: 12 }} ellipsis>
                {option?.label ?? value}
              </Text>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={value}>
            <Text style={{ fontSize: 12 }} ellipsis>
              {value}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (value: string) => (
        <Tooltip title={value}>
          <Text style={{ fontSize: 12 }}>{value}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (value: boolean) => (
        <Tag color={value ? 'green' : 'default'} style={{ fontSize: 11 }}>{value ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Intensity',
      dataIndex: 'rain_intensity',
      key: 'rain_intensity',
      width: 100,
      align: 'center',
      render: (value: number | null) => (
        <Text style={{ fontSize: 12 }}>{value !== null ? `Level ${value}` : '-'}</Text>
      ),
    },
    {
      title: 'Modified',
      dataIndex: 'was_modified',
      key: 'was_modified',
      width: 100,
      align: 'center',
      render: (value: boolean | null) => value ? <Tag color="blue" style={{ fontSize: 11 }}>Yes</Tag> : <Text style={{ fontSize: 12 }}>-</Text>,
    },
  ];

  // Add actions column for users who can manage
  if (canManageForecasts) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          {record.deleted ? (
            <Tooltip title="Restore">
              <Button 
                type="text" 
                icon={<UndoOutlined />} 
                onClick={() => handleRestore(record)}
              />
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Edit">
                <Button 
                  type="text" 
                  icon={<EditOutlined />} 
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button 
                  type="text" 
                  danger
                  icon={<DeleteOutlined />} 
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    });
  }

  // Render empty state
  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px' }}>
        <Card style={{ flex: 1 }}>
          <Empty description="Select an event to view rain forecasts" />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Timeline with integrated header */}
      <div style={{ flexShrink: 0 }}>
        <RainForecastsTimeline
          forecasts={displayedForecasts}
          sessions={selectedEvent?.sessions || []}
          eventStartDate={selectedEvent.start_date}
          eventEndDate={selectedEvent.end_date}
          circuitTimeZone={circuitTimeZone}
          loading={loading}
          refreshing={refreshing}
          onRefresh={() => fetchForecasts(false, true)}
          onCreateForecast={canManageForecasts ? handleCreate : undefined}
          canManageForecasts={!!canManageForecasts}
          isEventActive={isEventActive}
          hasAdminRole={hasAdminRole}
          error={error}
          onClearError={() => setError(null)}
        />
      </div>

      {hasAdminRole && (
        <div style={{ padding: '4px 12px', flexShrink: 0 }}>
          <Checkbox
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          >
            Include deleted
          </Checkbox>
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Card style={{ textAlign: 'center', padding: 40, borderRadius: 0 }}>
            <Spin size="large" />
          </Card>
        ) : displayedForecasts.length === 0 ? (
          <Card style={{ flex: 1, borderRadius: 0 }}>
            <Empty description="No rain forecasts found for this event" />
          </Card>
        ) : (
          <Card 
            style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }} 
            styles={{ body: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
          >
            {/* Info banner for legacy events */}
            {selectedEvent?.legacy && (
              <div style={{ 
                marginBottom: 0, 
                padding: '6px 12px', 
                background: 'rgba(255, 165, 0, 0.1)', 
                borderRadius: 0,
                border: '1px solid rgba(255, 165, 0, 0.3)',
                flexShrink: 0,
              }}>
                <Text type="secondary">
                  📜 This is a legacy event. Rain forecasts are read-only from historical data.
                </Text>
              </div>
            )}

            {/* Info banner for non-active prod events */}
            {!isEventActive && isNonLegacyEvent && hasAdminRole && !isDemo && (
              <div style={{ 
                marginBottom: 0, 
                padding: '6px 12px', 
                background: 'rgba(24, 144, 255, 0.1)', 
                borderRadius: 0,
                border: '1px solid rgba(24, 144, 255, 0.3)',
                flexShrink: 0,
              }}>
                <Text type="secondary">
                  📅 This event is not currently active. Rain forecasts can only be added during the event dates.
                </Text>
              </div>
            )}

            {/* Demo ended: clarify debug CRUD is allowed */}
            {!isEventActive && isNonLegacyEvent && hasAdminRole && isDemo && (
              <div style={{ 
                marginBottom: 0, 
                padding: '6px 12px', 
                background: 'rgba(82, 196, 26, 0.08)', 
                borderRadius: 0,
                border: '1px solid rgba(82, 196, 26, 0.25)',
                flexShrink: 0,
              }}>
                <Text type="secondary">
                  Demo event ended: you can still add or edit rain forecasts for debugging (writes go to demo data).
                </Text>
              </div>
            )}

            <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
              <Table
                dataSource={displayedForecasts}
                columns={columns}
                rowKey={(record) => `${record.value_date}-${record.created_at}`}
                size="small"
                scroll={{ x: 900, ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} forecasts`,
              }}
              style={{ flex: 1 }}
              onRow={(record) => ({
                style: record.deleted ? { opacity: 0.5, textDecoration: 'line-through' } : undefined,
              })}
            />
            </div>
          </Card>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={editingForecast ? 'Edit Rain Forecast' : 'Add Rain Forecast'}
        open={isModalOpen}
        onCancel={handleModalCancel}
        onOk={handleSubmit}
        confirmLoading={submitting}
        width={640}
        forceRender
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', paddingRight: 8 } }}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          size="middle"
        >
          {/* Row 1: Forecast Time + Session */}
          <Space style={{ width: '100%', display: 'flex' }} size={16}>
            <Form.Item
              name="value_date"
              label="Forecast Time"
              rules={[{ required: true, message: 'Required' }]}
              tooltip="The time this forecast is valid for."
              style={{ flex: 1, marginBottom: 12 }}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name="session_type"
              label="Session"
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1, marginBottom: 12 }}
            >
              <Select options={modalSessionSelectOptions} optionFilterProp="label" showSearch />
            </Form.Item>
          </Space>

          {/* Message */}
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please enter forecast message' }]}
            style={{ marginBottom: 12 }}
          >
            <TextArea 
              rows={2} 
              placeholder="e.g., No rain expected in the next 30mn" 
              maxLength={500}
              showCount
            />
          </Form.Item>

          {/* Row 2: Active, Rain Type, Few Drops */}
          <Space style={{ width: '100%', display: 'flex' }} size={16}>
            <Form.Item
              name="is_active"
              label="Active"
              valuePropName="checked"
              tooltip="Active forecasts contain weather info"
              style={{ marginBottom: 12 }}
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>

            <Form.Item
              name="rain_type"
              label="Rain Type"
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1, marginBottom: 12 }}
            >
              <Select options={rainTypeOptions} />
            </Form.Item>

            <Form.Item
              name="few_drops"
              label="Few Drops"
              valuePropName="checked"
              tooltip="Light occasional drops"
              style={{ marginBottom: 12 }}
            >
              <Switch />
            </Form.Item>
          </Space>

          {/* Optional Fields - Collapsible Section */}
          <div style={{ 
            border: '1px solid #d9d9d9', 
            borderRadius: 6, 
            padding: 12, 
            marginBottom: 8,
            background: 'rgba(0,0,0,0.02)'
          }}>
            <Title level={5} style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
              Advanced Details (Optional)
            </Title>

            {/* Row: Intensity + Pattern */}
            <Space style={{ width: '100%', display: 'flex' }} size={16}>
              <Form.Item
                name="rain_intensity"
                label="Rain Intensity"
                tooltip="1=light, 3=heavy"
                style={{ flex: 1, marginBottom: 12 }}
              >
                <Slider
                  min={1}
                  max={3}
                  marks={{ 1: '1', 2: '2', 3: '3' }}
                />
              </Form.Item>

              <Form.Item
                name="rain_pattern"
                label="Rain Pattern"
                style={{ flex: 1, marginBottom: 12 }}
              >
                <Select options={rainPatternOptions} allowClear placeholder="Select" />
              </Form.Item>
            </Space>

            <Space style={{ width: '100%', display: 'flex' }} size={16}>
              <Form.Item
                name="rain_speed_kph"
                label="Wind Speed (kph)"
                style={{ flex: 1, marginBottom: 12 }}
              >
                <InputNumber min={0} max={200} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="rain_direction_deg"
                label="Wind Direction (°)"
                style={{ flex: 1, marginBottom: 12 }}
              >
                <InputNumber min={0} max={360} style={{ width: '100%' }} />
              </Form.Item>
            </Space>

            <Space style={{ width: '100%', display: 'flex' }} size={16}>
              <Form.Item
                name="rain_start_time"
                label="Rain Start Time"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input placeholder="e.g., 14:30" />
              </Form.Item>

              <Form.Item
                name="rain_end_time"
                label="Rain End Time"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input placeholder="e.g., 16:00" />
              </Form.Item>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

